import React, { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { isBrowser } from '../../Utils/Helpers';

// Services
import {
  capitalizeFirstLetter,
  getMetaDataTableValuesAsoUnit,
  getMetaDataTableValuesRentUnit,
  getUnitExtraInfoMessages,
  getAvailableStatusMessagesCostUnit,
  RULE_FOR_EMPTY_VALUE,
  translateBuildingType,
} from '../services/helpers.services';

// Components
import ButtonLink from './ButtonLink';
import HeaderImage from './HeaderImage';
import svg from '../../svg';
import Cookies from 'js-cookie';
import { messages } from '../services/translations.service';

const UnitCard = ({ data, searchType }) => {

  const intl = useIntl();
  const [metaDataTableValues, setMetaDataTableValues] = useState([]);

  useEffect(() => {
    'aso' === searchType
      ? setMetaDataTableValues(getMetaDataTableValuesAsoUnit(data))
      : setMetaDataTableValues(getMetaDataTableValuesRentUnit(data));
  }, []);

  const CityAreaComponent = () => {
    return (
      <div key={'city-areas-component-' + data.post_id}>
        {data.unit_city && data.unit_district && (
          <h3 className='text-lg font-normal text-green mb-4'>
            {data.unit_city},{' '}
            {capitalizeFirstLetter(data.unit_district.toLowerCase())}
          </h3>
        )}
      </div>
    );
  };

  const VolumesComponent = () => {
    let sizes = null;
    if (data.unit_apartment_sizes !== null) {
      const sizesVal = data.unit_apartment_sizes.split('-');
      sizes = `${parseFloat(sizesVal[0])} - ${parseFloat(sizesVal[1])}`;
      sizes = sizes.replaceAll('.', ',');
    }

    const buildingTypes = [];
    for (let i = 1; i <= 5; i++) {
      if (data[`unit_building_type_${i}`]) {
        buildingTypes.push(
          translateBuildingType(data[`unit_building_type_${i}`])
        );
      }
    }

    return (
      <div
        key={'volumes-component-' + data.post_id}
        className='flex justify-between align-center mb-4 text-lg'
      >
        <span className='font-bold whitespace-nowrap'>
          {sizes !== null && (
            <>
              {sizes} m<sup>2</sup>
            </>
          )}
        </span>
        <span className='font-normal text-right'>
          {data.unit_apartment_types && data.unit_apartment_types}
          {!!buildingTypes.length &&
            buildingTypes.map((strObject, index) => (
              <span key={`building-type-${index}`}>
                {index < buildingTypes.length ? ', ' : ''}
                {strObject}
              </span>
            ))}
        </span>
      </div>
    );
  };

  const shortenEngLabel = (data) => {
    const shortcuts = {
      payment: 'pymt',
      month: 'mo',
    };

    const locale = isBrowser() ? Cookies.get('pll_language') : 'fi';
    const labelKey = data.props.id;

    return messages(locale)[labelKey]?.replace(/:([a-zA-Z]+)/g, (m, i) =>
      i in shortcuts ? shortcuts[i] : m
    );
  };

  const MetaTableComponentRow = (data) => {
    return data
      .filter((obj) => {
        return !(parseInt(obj.value) === 0 || !obj.value);
      })
      .map((metaObj) => {
        return (
          <div key={metaObj.value} className='flex justify-between'>
            {/* Highlight these special labels eg. "Varallisuusrajat" */}
            {metaObj.value === RULE_FOR_EMPTY_VALUE ? (
              <strong className='mt-4 block'>{metaObj.label}</strong>
            ) : (
              <span>{shortenEngLabel(metaObj.label)}</span>
            )}
            {/* the RULE_FOR_EMPTY_VALUE means that we only want to show the label without the corresponding value. */}
            {metaObj.value !== RULE_FOR_EMPTY_VALUE && (
              <span>{metaObj.value}</span>
            )}
          </div>
        );
      });
  };

  const MetaTableComponent = () => {
    return (
      <div
        key={'meta-table-component-' + data.post_id}
        className='align-center mb-9 text-lg'
      >
        {MetaTableComponentRow(metaDataTableValues)}
        {extraInfoComponent()}
      </div>
    );
  };

  const extraInfoComponent = () => {
    const message = getUnitExtraInfoMessages(data, searchType);
    if (message) {
      return (
        <div className='mt-4'>
          <span>{message}</span>
        </div>
      );
    }
    return false;
  };

  let imageData = null;
  if (data.unit_image) {
    imageData = {
      url: JSON.parse(data.unit_image)?.url,
      hash: JSON.parse(data.unit_image)?.hash,
    };
  } else if (data.ap_image) {
    imageData = {
      url: JSON.parse(data.ap_image)?.url,
      hash: JSON.parse(data.ap_image)?.hash,
    };
  }

  const statusText = getAvailableStatusMessagesCostUnit(data);

  const unitActuallyPlanned =
    data.unit_is_planned == 1 &&
    data.unit_completion_date &&
    new Date(data.unit_completion_date) > new Date();
  // Hide the basic text in one condition
  let textInfo = unitActuallyPlanned ? null : (
    <FormattedMessage id='UNIT_TEXT_INFO' />
  );

  const buttonAriaLabel = intl.formatMessage({id:'UNIT_MORE_BUTTON_ARIA_LABEL'}, {
    address: data.unit_address,
  });

  return (
    <article className='w-full md:w-6/12 xl:w-4/12 mb-11 lg:mb-20 lg:col-span-4 text-left'>
      <div className='mx-0 md:mx-gutter bg-grey-100'>
        {imageData ? (
          <HeaderImage
            ap_url={data.ap_url}
            key={imageData.hash}
            url={imageData.url}
            type={searchType}
            statusInfo={statusText}
            textInfo={textInfo}
            address={data.unit_address}
          />
        ) : (
          <HeaderImage
            key={Math.random().toString(36).substring(2, 7)}
            ap_url={data.ap_url}
            usePlaceholder={true}
            type={searchType}
            statusInfo={statusText}
            textInfo={textInfo}
            address={data.unit_address}
          />
        )}
        <main className='mx-mobile-gutter lg:mx-gutter py-9 pb-14'>
          {CityAreaComponent()}
          <a href={data.unit_url}>
            <h3 className='text-3xl mb-4'>{data.unit_address}</h3>
          </a>
          {VolumesComponent()}
          {MetaTableComponent()}
          <div>
            <ButtonLink
              key={data.unit_url}
              url={data.unit_url}
              extraClasses={unitActuallyPlanned ? 'btn-green-400' : null}
              ariaLabel={buttonAriaLabel}
              label={
                unitActuallyPlanned ? (
                  <FormattedMessage id='UNIT_PLANNED_BUTTON_LABEL' />
                ) : (
                  <FormattedMessage id='UNIT_MORE_BUTTON_LABEL' />
                )
              }
              icon={{
                classes: 'fill-white w-8',
                svg: svg('long-arrow-alt-right-regular'),
              }}
            />
          </div>
        </main>
      </div>
    </article>
  );
};

export default UnitCard;
